import { LitElement, html } from "lit";

import { customElement, property, query, state } from "lit/decorators.js";

import { autoUpdate, computePosition, flip, shift } from "@floating-ui/dom";
import type { Placement } from "@floating-ui/dom";

import { dropdownMenuStyles } from "./dropdown-menu.css";

@customElement("dropdown-menu")
export class DropdownMenu extends LitElement {
  static override styles = [dropdownMenuStyles];

  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: String }) placement: Placement = "bottom-start";

  @state() private hasSlottedItems = false;
  @state() private popoverId = `dropdown-${Math.random().toString(36).substr(2, 9)}`;

  @query(".dropdown-trigger") private triggerElement!: HTMLElement;
  @query(".dropdown-content") private dropdownElement!: HTMLElement;

  private autoUpdateCleanup?: () => void;

  private setupFloatingUI() {
    if (!this.triggerElement || !this.dropdownElement) return;

    this.autoUpdateCleanup = autoUpdate(this.triggerElement, this.dropdownElement, () => {
      computePosition(this.triggerElement, this.dropdownElement, {
        placement: this.placement,
        strategy: "fixed",
        middleware: [flip(), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        Object.assign(this.dropdownElement.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    });
  }

  private cleanupFloatingUI() {
    this.autoUpdateCleanup?.();
    this.autoUpdateCleanup = undefined;
  }

  private handlePopoverToggle = (e: ToggleEvent) => {
    // Update the reflected open state based on popover state
    this.setAttribute("open", e.newState === "open" ? "" : "false");

    if (e.newState === "open") {
      // Setup floating-ui positioning when popover opens
      this.setupFloatingUI();
    } else {
      // Cleanup when popover closes
      this.cleanupFloatingUI();
    }
  };

  private handleDropdownItemClick = (e: Event) => {
    e.stopPropagation();
    this.cleanupFloatingUI();

    // Close dropdown when item is clicked
    this.dropdownElement?.hidePopover();

    // Re-emit the event so parent components can handle it
    this.dispatchEvent(
      new CustomEvent("dropdown-item-click", {
        detail: (e as CustomEvent).detail,
        bubbles: true,
        composed: true,
      }),
    );
  };

  private handleSlotChange = () => {
    const slot = this.shadowRoot?.querySelector("slot:not([name])") as HTMLSlotElement;
    const assignedElements = slot?.assignedElements() || [];
    this.hasSlottedItems = assignedElements.length > 0;
  };

  protected override render() {
    return html`
      <div class="dropdown-container" @dropdown-item-click=${this.handleDropdownItemClick}>
        <button
          class="dropdown-trigger"
          type="button"
          ?disabled=${this.disabled}
          popovertarget=${this.popoverId}
          aria-haspopup="menu"
        >
          <slot name="trigger"></slot>
        </button>

        <div
          id=${this.popoverId}
          class="dropdown-content"
          popover="auto"
          role="menu"
          @toggle=${this.handlePopoverToggle}
        >
          <slot @slotchange=${this.handleSlotChange}></slot>
        </div>
      </div>
    `;
  }

  protected override firstUpdated() {
    // Initialize slot state on first render
    this.handleSlotChange();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "dropdown-menu": DropdownMenu;
  }
}
